// 10 Enterprise Health Assessment modules.
// Each module has 8 dimensions, each assessed by 1 Likert question (1-5)

const make = (slug, name, accent, headline, subhead, dimensions) => ({
  slug, name, accent, headline, subhead,
  dimensions: dimensions.map(d => ({ key: d[0], label: d[0], question: d[1] }))
});

export const ASSESSMENT_MODULES = [
  make(
    "strategic",
    "Strategic Health",
    "#0F4C3A",
    "Is your business strategy built for sustainable growth?",
    "Evaluate strategic direction, leadership, competitive positioning, market adaptability, innovation capability, and long-term sustainability.",
    [
      ["Vision Alignment", "Our organization has a clear vision that is well-understood across all levels."],
      ["Strategic Planning", "We have a documented, dynamic strategic plan that is reviewed regularly."],
      ["Market Position", "We have a defensible market position with clear differentiation."],
      ["Competitive Advantage", "Our competitive advantages are sustainable and difficult to replicate."],
      ["Leadership Effectiveness", "Our leadership team executes strategy effectively and inspires the organization."],
      ["Innovation Capacity", "We have a structured mechanism to nurture and commercialize innovation."],
      ["Business Model Strength", "Our business model is resilient, scalable, and proven."],
      ["Growth Readiness", "We are operationally prepared to capture the next stage of growth."],
    ]
  ),
  make(
    "financial",
    "Financial Health",
    "#1A6B53",
    "Understand the financial strength of your organization.",
    "Identify financial risks, cash flow efficiency, profitability trends, capital utilization, and long-term financial sustainability.",
    [
      ["Revenue Stability", "Our revenue base is diversified and stable across customers/segments."],
      ["Cash Flow", "Our operating cash flow consistently funds operations and growth."],
      ["Profitability", "Our profit margins meet or exceed industry benchmarks."],
      ["Working Capital", "We efficiently manage receivables, payables and inventory."],
      ["Capital Structure", "Our capital structure is optimal and supports our growth ambitions."],
      ["Investment Readiness", "We have robust financials and reporting to attract institutional capital."],
      ["Cost Optimization", "We actively monitor and optimize cost structures."],
      ["Financial Risk", "We have early-warning systems for financial and credit risk."],
    ]
  ),
  make(
    "operational",
    "Operational Excellence",
    "#0F4C3A",
    "Evaluate operational performance across your enterprise.",
    "Measure efficiency, productivity, quality, process maturity, and resource utilization.",
    [
      ["Operational Efficiency", "Our core processes operate at high efficiency with minimal waste."],
      ["Process Standardization", "Our processes are standardized, documented and consistently followed."],
      ["Productivity", "Workforce productivity is measured and continuously improving."],
      ["Quality Control", "Quality is built into our processes — defects are rare and isolated."],
      ["Capacity Utilization", "We utilize our capacity optimally and forecast demand accurately."],
      ["Process Maturity", "Our business processes have reached high maturity (defined / managed / optimized)."],
      ["Performance Metrics", "Operational KPIs are visible, tracked and acted upon."],
      ["Continuous Improvement", "We have a structured continuous improvement culture (Lean / Six Sigma)."],
    ]
  ),
  make(
    "risk",
    "Enterprise Risk",
    "#0F4C3A",
    "Identify risks before they become business problems.",
    "Assess strategic, operational, financial, technological, regulatory, and reputational risks.",
    [
      ["Enterprise Risk", "We maintain an enterprise risk register that is reviewed by leadership."],
      ["Operational Risk", "Operational risks are identified, owned and mitigated proactively."],
      ["Financial Risk", "Financial risks (FX, credit, liquidity) are quantified and hedged appropriately."],
      ["Regulatory Risk", "We are well-prepared for regulatory changes in our markets."],
      ["Cyber Risk", "Our cybersecurity posture is mature with regular testing."],
      ["Supply Chain Risk", "We have visibility, redundancy and resilience across our supply chain."],
      ["Reputation Risk", "We monitor brand/reputation risk and respond rapidly to incidents."],
      ["Business Continuity", "We have a tested business continuity and disaster recovery plan."],
    ]
  ),
  make(
    "governance",
    "Corporate Governance",
    "#0F4C3A",
    "Measure governance effectiveness.",
    "Evaluate governance structures, board effectiveness, compliance culture and accountability.",
    [
      ["Governance Framework", "Our governance framework is documented, current and well-understood."],
      ["Internal Controls", "Internal controls are designed and operating effectively across processes."],
      ["Board Governance", "Our board is independent, diverse and actively oversees strategy and risk."],
      ["Decision Authority", "Authority matrices are clear and consistently applied."],
      ["Ethics & Compliance", "Our ethics program is mature with strong tone-at-the-top."],
      ["Delegation Matrix", "Delegation of authority is balanced — empowering without losing oversight."],
      ["Audit Readiness", "We are continuously audit-ready across financial and operational dimensions."],
      ["Policy Framework", "Policies are kept current, accessible and enforced."],
    ]
  ),
  make(
    "digital",
    "Digital Transformation",
    "#1A6B53",
    "Measure digital maturity.",
    "Assess preparedness for AI, automation, digital operations, cloud, analytics and cybersecurity.",
    [
      ["AI Readiness", "We have a clear strategy and use-cases for AI adoption."],
      ["Automation", "We systematically automate repetitive workflows and processes."],
      ["Digital Processes", "Our customer-facing and back-office processes are digital-first."],
      ["ERP Maturity", "Our core ERP/IT systems are modern, integrated and reliable."],
      ["Data Analytics", "We make decisions using timely, accurate analytics."],
      ["Cloud Adoption", "Our infrastructure is cloud-native or cloud-forward."],
      ["Cybersecurity", "Our cybersecurity matches industry best practice."],
      ["Technology Strategy", "Our technology investments align with business strategy."],
    ]
  ),
  make(
    "human-capital",
    "Human Capital",
    "#1A6B53",
    "Evaluate organizational capability.",
    "Measure engagement, leadership pipeline, culture, talent, succession and performance.",
    [
      ["Culture", "We have a strong, intentional culture aligned with our values."],
      ["Engagement", "Employee engagement is high, measured and acted upon."],
      ["Leadership", "We have strong leadership at every level."],
      ["Skills Gap", "Critical skill gaps are identified and being closed."],
      ["Succession Planning", "We have credible successors identified for critical roles."],
      ["Workforce Planning", "We plan workforce capacity ahead of business needs."],
      ["Performance Management", "Performance management is fair, transparent and developmental."],
      ["Learning & Development", "We invest meaningfully in learning and growth."],
    ]
  ),
  make(
    "esg",
    "ESG & Sustainability",
    "#0F4C3A",
    "Measure sustainable business performance.",
    "Evaluate environmental, social, governance and sustainability practices against global standards.",
    [
      ["ESG Strategy", "ESG is integrated into our corporate strategy."],
      ["Carbon Footprint", "We measure, report and actively reduce our carbon footprint."],
      ["Governance", "ESG governance and accountability are clearly defined."],
      ["Social Responsibility", "We have a meaningful social impact and community engagement."],
      ["Sustainable Operations", "Our operations apply circular/sustainable principles."],
      ["Reporting Readiness", "We are ready for upcoming ESG disclosure requirements (BRSR / CSRD)."],
      ["Stakeholder Engagement", "We engage stakeholders systematically on ESG topics."],
      ["ESG Risk", "ESG risks are integrated into our enterprise risk framework."],
    ]
  ),
  make(
    "market",
    "Market Competitiveness",
    "#C5A265",
    "Understand your competitive position.",
    "Benchmark against competitors, market trends, customer expectations, pricing and innovation.",
    [
      ["Market Share", "Our market share is growing in our chosen segments."],
      ["Customer Experience", "Our customer experience is best-in-class and measured (NPS/CSAT)."],
      ["Brand Position", "Our brand has strong recall and preference in our markets."],
      ["Competitive Intelligence", "We have systematic competitive intelligence informing decisions."],
      ["Product Strategy", "Our product/service roadmap is differentiated and credible."],
      ["Pricing Strategy", "Our pricing is strategic — capturing value without sacrificing share."],
      ["Innovation", "We innovate ahead of our competitors and customers."],
      ["Customer Retention", "Our customer retention rates are strong and improving."],
    ]
  ),
  make(
    "growth",
    "Business Growth Readiness",
    "#C5A265",
    "Is your organization ready for the next stage of growth?",
    "A condensed executive-level evaluation across the most important readiness factors.",
    [
      ["Vision & Strategy", "We have a credible plan to reach our next growth milestone."],
      ["Operating Model", "Our operating model can scale 2-3x without breaking."],
      ["Talent", "We have the talent and leadership ready for the next stage."],
      ["Capital", "We have the capital plan and access to fund growth."],
      ["Systems", "Our systems & data can support the next stage of growth."],
      ["Market Demand", "Demand signals support the planned growth trajectory."],
      ["Risk Posture", "Our risk posture is calibrated for ambition without fragility."],
      ["Governance", "Governance keeps pace with our scale and ambition."],
    ]
  ),
];

export const LIKERT = [
  { value: 1, label: "Strongly Disagree" },
  { value: 2, label: "Disagree" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Agree" },
  { value: 5, label: "Strongly Agree" },
];

export function computeScore(answers, dimensions) {
  const arr = dimensions.map(d => answers[d.key] || 0);
  const filled = arr.filter(v => v > 0);
  const max = dimensions.length * 5;
  const sum = arr.reduce((a, b) => a + b, 0);
  const pct = max ? Math.round((sum / max) * 100) : 0;
  return { sum, max, pct, answered: filled.length, total: dimensions.length };
}

export function tierForScore(pct) {
  if (pct >= 85) return { tier: "Leader", color: "#0F4C3A", desc: "Industry-leading maturity. Sustain and selectively expand." };
  if (pct >= 70) return { tier: "Advanced", color: "#1A6B53", desc: "Strong maturity with targeted opportunities to harden." };
  if (pct >= 55) return { tier: "Developing", color: "#C5A265", desc: "Solid foundations — focus on consistency and breadth." };
  if (pct >= 40) return { tier: "Emerging", color: "#B07A3A", desc: "Capabilities exist but are uneven. Prioritize standardization." };
  return { tier: "At Risk", color: "#B33A3A", desc: "Significant gaps. Immediate intervention recommended." };
}

export function getModule(slug) {
  return ASSESSMENT_MODULES.find(m => m.slug === slug);
}
